import { normalizeE164 } from "./outboundCallRequest.ts";

export interface DncListEntry {
  phone_number: string;
  status?: string | null;
  expires_at?: string | null;
}

export type DncDecision =
  | { blocked: false }
  | { blocked: true; reason: "dnc" | "dnc_check_failed" };

const DNC_ROW_CAP = 10000;

type DncQuery = {
  eq: (column: string, value: string) => DncQuery;
  limit: (count: number) => DncQuery;
  then: PromiseLike<{
    data: DncListEntry[] | null;
    error: { message: string } | null;
  }>["then"];
};

export type DncClient = {
  from: (table: string) => {
    select: (columns: string) => DncQuery;
  };
};

/** Compare stored DNC numbers with the number about to be dialed. */
export function canonicalDncPhone(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const text = String(raw).trim();
  if (!text) return null;

  const direct = normalizeE164(text);
  const withoutTrunkZero = normalizeE164(
    text.replace(/\(0\)/g, "").replace(/\+440/g, "+44"),
  );

  if (direct?.startsWith("+440") && withoutTrunkZero && !withoutTrunkZero.startsWith("+440")) {
    return withoutTrunkZero;
  }
  return direct ?? withoutTrunkZero;
}

export function dncNumbersMatch(left: string, right: string): boolean {
  const a = canonicalDncPhone(left);
  const b = canonicalDncPhone(right);
  if (a && b) return a === b;

  const leftDigits = left.replace(/\D/g, "");
  const rightDigits = right.replace(/\D/g, "");
  return leftDigits.length >= 8 && leftDigits === rightDigits;
}

export function entryBlocksNumber(
  entry: DncListEntry,
  phone: string,
  now: Date = new Date(),
): boolean {
  if ((entry.status ?? "Active").toLowerCase() !== "active") return false;
  if (entry.expires_at) {
    const expiry = new Date(entry.expires_at);
    if (!Number.isNaN(expiry.getTime()) && expiry.getTime() <= now.getTime()) return false;
  }
  return dncNumbersMatch(entry.phone_number, phone);
}

/**
 * Fail closed. A lookup error, a missing user, or a list at the row cap
 * blocks the dial instead of placing it.
 */
export async function decideDncBlock(
  supabase: any,
  userId: string | null | undefined,
  phone: string | null | undefined,
  now: Date = new Date(),
): Promise<DncDecision> {
  if (!userId || !phone?.trim()) {
    return { blocked: true, reason: "dnc_check_failed" };
  }

  const { data, error } = await supabase
    .from("dnc_entries")
    .select("phone_number, status, expires_at")
    .eq("user_id", userId)
    .eq("status", "Active")
    .limit(DNC_ROW_CAP);

  if (error) return { blocked: true, reason: "dnc_check_failed" };
  const rows = data ?? [];
  if (rows.length >= DNC_ROW_CAP) return { blocked: true, reason: "dnc_check_failed" };
  if (rows.some((entry: DncListEntry) => entryBlocksNumber(entry, phone, now))) {
    return { blocked: true, reason: "dnc" };
  }
  return { blocked: false };
}
